import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeAnalysis = ({ codeAnalysis }) => {
  const [activeTab, setActiveTab] = useState('suggestions');

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'AlertTriangle';
      case 'low':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  const tabs = [
    { id: 'suggestions', label: 'Suggestions', icon: 'Lightbulb', count: codeAnalysis?.suggestions?.length },
    { id: 'bestPractices', label: 'Best Practices', icon: 'Award', count: codeAnalysis?.bestPractices?.length },
    { id: 'patterns', label: 'Patterns', icon: 'Code', count: codeAnalysis?.patterns?.length }
  ];

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="FileText" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">Code Analysis</h2>
      </div>
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-subtle">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab?.id
                ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="font-medium">{tab?.label}</span>
            {tab?.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab?.id
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {tab?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab === 'suggestions' && (
          <div className="space-y-4">
            {codeAnalysis?.suggestions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
                <p>Great job! No suggestions for improvement.</p>
              </div>
            ) : (
              codeAnalysis?.suggestions?.map((suggestion, index) => (
                <div key={index} className="border border-subtle rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon 
                      name={getSeverityIcon(suggestion?.severity)} 
                      size={20} 
                      color={getSeverityColor(suggestion?.severity)?.replace('text-', 'var(--color-')}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-foreground">{suggestion?.title}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          suggestion?.severity === 'high' ? 'bg-error/20 text-error' :
                          suggestion?.severity === 'medium'? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                        }`}>
                          {suggestion?.severity?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {suggestion?.description}
                      </p>
                      {suggestion?.codeExample && (
                        <div className="bg-muted/50 rounded p-3 mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Code" size={14} />
                            <span className="text-xs font-medium text-foreground">Suggested Code:</span>
                          </div>
                          <pre className="text-xs text-code overflow-x-auto">
                            {suggestion?.codeExample}
                          </pre>
                        </div>
                      )}
                      {suggestion?.documentation && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="ExternalLink"
                          iconPosition="right"
                          onClick={() => window.open(suggestion?.documentation, '_blank')}
                        >
                          View Documentation
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'bestPractices' && (
          <div className="space-y-4">
            {codeAnalysis?.bestPractices?.map((practice, index) => (
              <div key={index} className="border border-subtle rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    practice?.followed ? 'bg-success/20' : 'bg-warning/20'
                  }`}>
                    <Icon 
                      name={practice?.followed ? 'Check' : 'X'} 
                      size={16} 
                      color={practice?.followed ? 'var(--color-success)' : 'var(--color-warning)'}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-2">{practice?.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {practice?.description}
                    </p>
                    {!practice?.followed && practice?.recommendation && (
                      <div className="bg-warning/10 border border-warning/20 rounded p-3">
                        <p className="text-sm text-warning">{practice?.recommendation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="space-y-4">
            {codeAnalysis?.patterns?.map((pattern, index) => (
              <div key={index} className="border border-subtle rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Puzzle" size={16} color="var(--color-secondary)" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-2">{pattern?.name}</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {pattern?.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="TrendingUp" size={12} />
                        <span>Difficulty: {pattern?.difficulty}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        <span>Usage: {pattern?.usage}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeAnalysis;